import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";

import PageSection from "./PageSection";
import SectionHeader from "../components/SectionHeader";
import api from "../api";
import { useLanguage } from "../context/LanguageContext";
import { radius, sectionColors } from "../styles/tokens";
import { useSiteTheme } from "../theme/ThemeContext";

interface StudioContent {
  studio_title_en: string;
  studio_title_ro: string;
  studio_content_en: string;
  studio_content_ro: string;
}

const storyImages = [
  {
    src: "/images/story/01-preparing-the-design-450.webp",
    altEn: "Preparing the design for a monastery mural",
    altRo: "Pregătirea desenului pentru o pictură murală de mănăstire",
  },
  {
    src: "/images/story/02-painting-the-ceiling-450.webp",
    altEn: "Painting a monastery ceiling",
    altRo: "Pictarea tavanului unei mănăstiri",
  },
  {
    src: "/images/story/03-working-at-height-450.webp",
    altEn: "Working at height on a monastery mural",
    altRo: "Lucrând la înălțime la o pictură murală de mănăstire",
  },
  {
    src: "/images/story/04-completed-mural-450.webp",
    altEn: "Standing before a completed monumental mural",
    altRo: "În fața unei picturi murale monumentale finalizate",
  },
  {
    src: "/images/story/05-a-moments-rest-450.webp",
    altEn: "A moment of rest on the scaffolding",
    altRo: "Un moment de odihnă pe schelă",
  },
  {
    src: "/images/story/06-hand-painted-icon-450.webp",
    altEn: "A small hand-painted icon",
    altRo: "O mică icoană pictată manual",
  },
];

export default function StudioSection() {
  const { language } = useLanguage();

  const [content, setContent] = useState<StudioContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previousImageIndex, setPreviousImageIndex] = useState<number | null>(
    null,
  );
  const [storyIsVisible, setStoryIsVisible] = useState(false);

  const storyImageRef = useRef<HTMLDivElement | null>(null);
  const { palette } = useSiteTheme();

  useEffect(() => {
    const fetchStudioContent = async (): Promise<void> => {
      try {
        setLoading(true);

        const response = await api.get<StudioContent>("/content");

        setContent(response.data);
        setError("");
      } catch (error) {
        console.error("Failed to load Studio content:", error);
        setError("Failed to load Studio content");
        setContent(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchStudioContent();
  }, []);

  useEffect(() => {
    const storyImageElement = storyImageRef.current;

    if (!storyImageElement) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setStoryIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.45,
      },
    );

    observer.observe(storyImageElement);

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  useEffect(() => {
    if (!storyIsVisible) {
      return;
    }

    const imageTimer = window.setInterval(() => {
      setCurrentImageIndex((currentIndex) => {
        setPreviousImageIndex(currentIndex);

        window.setTimeout(() => {
          setPreviousImageIndex(null);
        }, 1500);

        return (currentIndex + 1) % storyImages.length;
      });
    }, 7000);

    return () => {
      window.clearInterval(imageTimer);
    };
  }, [storyIsVisible]);

  const title =
    language === "ro"
      ? (content?.studio_title_ro ?? "")
      : (content?.studio_title_en ?? "");

  const studioText =
    language === "ro"
      ? (content?.studio_content_ro ?? "")
      : (content?.studio_content_en ?? "");

  return (
    <PageSection bg={palette.story}>
      <Box sx={{ maxWidth: 1000, mx: "auto" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={300}
          >
            <CircularProgress />
          </Box>
        ) : error || !content ? (
          <Alert severity="error">
            {error || "Studio content is unavailable"}
          </Alert>
        ) : (
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <SectionHeader sx={{ mb: 2, textAlign: "left" }}>
                  {title}
                </SectionHeader>

                {studioText
                  .split("\n")
                  .filter((paragraph) => paragraph.trim() !== "")
                  .map((paragraph, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {paragraph}
                    </Typography>
                  ))}

                <Button
                  size="small"
                  component={RouterLink}
                  to="/about"
                  sx={{ mt: 1 }}
                >
                  {language === "ro" ? "Citește povestea mea" : "Read My Story"}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                ref={storyImageRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "2 / 3",
                  overflow: "hidden",
                  borderRadius: radius.surface,
                  bgcolor: "palette.paper",
                }}
              >
                {previousImageIndex !== null && (
                  <Box
                    component="img"
                    src={storyImages[previousImageIndex].src}
                    alt=""
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                )}

                <Box
                  key={storyImages[currentImageIndex].src}
                  component="img"
                  src={storyImages[currentImageIndex].src}
                  alt={
                    language === "ro"
                      ? storyImages[currentImageIndex].altRo
                      : storyImages[currentImageIndex].altEn
                  }
                  loading="lazy"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: 1,
                    animation: "storyFadeIn 1500ms ease-in-out",
                    "@keyframes storyFadeIn": {
                      from: {
                        opacity: 0,
                      },
                      to: {
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </PageSection>
  );
}
